import React, { useMemo, useState } from 'react';
import { Flex, Icon, Text, Button } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { MdAdd } from 'react-icons/md';
import { Schedule } from '../models/schedule';
import { toDateFormat } from '../utils/timestamp';
import ScheduleItem from './ScheduleItem';

interface ScheduleListProps {
  groupId: string;
  isMember: boolean;
  upcomingSchedules: Schedule[];
  pastSchedules: Schedule[];
}

const ScheduleList = ({
  groupId,
  isMember,
  upcomingSchedules,
  pastSchedules,
}: ScheduleListProps) => {
  const [isUpcoming, setIsUpcoming] = useState(true);
  const upcomingSchedulesByDate: Schedule[][] = useMemo(() => {
    let curDate: string;
    return upcomingSchedules.reduce<Schedule[][]>((acc, cur) => {
      const date = toDateFormat(cur.start);
      if (curDate === date) {
        return [...acc.slice(0, -1), [...acc[acc.length - 1], cur]];
      }
      curDate = date;
      return [...acc, [cur]];
    }, []);
  }, [upcomingSchedules]);
  const pastSchedulesByDate: Schedule[][] = useMemo(() => {
    let curDate: string;
    return pastSchedules.reduce<Schedule[][]>((acc, cur) => {
      const date = toDateFormat(cur.start);
      if (curDate === date) {
        return [...acc.slice(0, -1), [...acc[acc.length - 1], cur]];
      }
      curDate = date;
      return [...acc, [cur]];
    }, []);
  }, [pastSchedules]);

  const schedulesByDate = useMemo(
    () => (isUpcoming ? upcomingSchedulesByDate : pastSchedulesByDate),
    [isUpcoming, upcomingSchedulesByDate, pastSchedulesByDate]
  );

  return (
    <Flex flexDir="column" width="100%" pt="36px">
      <Flex justifyContent="space-between" alignItems="center">
        <Text flex="1" as="b" fontSize="1.5rem">
          모임 일정
        </Text>
        {isMember && (
          <Link to={`/group/${groupId}/schedule/add`}>
            <Button
              fontSize="12px"
              variant="outline"
              width="90px"
              height="28px"
              color="#D84E46"
              borderColor="#D84E46"
              _active={{
                bgColor: '#FFE5E4',
              }}
              _hover={{
                bgColor: '#FFE5E4',
              }}
            >
              <Icon as={MdAdd} /> 일정 만들기
            </Button>
          </Link>
        )}
      </Flex>
      <Flex mt="14px" mb="18px">
        <Button
          bgColor={isUpcoming ? '#343B51' : 'white'}
          borderColor={isUpcoming ? 'none' : '#E6E6E9'}
          color={isUpcoming ? '#E6E6E9' : '#303037'}
          fontWeight={isUpcoming ? 'bold' : 'normal'}
          borderRadius="35px"
          borderWidth="1px"
          onClick={() => setIsUpcoming(true)}
          mr="12px"
        >
          예정된 일정
        </Button>
        <Button
          bgColor={!isUpcoming ? '#343B51' : 'white'}
          borderColor={!isUpcoming ? 'none' : '#E6E6E9'}
          color={!isUpcoming ? '#E6E6E9' : '#303037'}
          fontWeight={!isUpcoming ? 'bold' : 'normal'}
          borderWidth="1px"
          borderRadius="35px"
          onClick={() => setIsUpcoming(false)}
        >
          종료된 일정
        </Button>
      </Flex>
      <Flex flexDir="column" mb="30px">
        {schedulesByDate.map(
          (dateGroup) =>
            dateGroup.length > 0 && (
              <Flex
                mb="1rem"
                flexDir="column"
                key={toDateFormat(dateGroup[0].start)}
              >
                <Text mb="12px" color="#858585" fontWeight="semibold">
                  {toDateFormat(dateGroup[0].start)}
                </Text>
                {dateGroup.map((schedule) => (
                  <Link
                    key={schedule.id}
                    to={`/group/${groupId}/schedule/${schedule.id}`}
                    style={{ width: '100%' }}
                  >
                    <ScheduleItem schedule={schedule} />
                  </Link>
                ))}
              </Flex>
            )
        )}
      </Flex>
    </Flex>
  );
};

export default ScheduleList;
